import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { authAPI, User } from '../api';
import { loadStripeClient } from '../utils/loadStripeClient';

export const IDENTITY_SESSION_KEY = 'buydrop_identity_session_id';

type Props = {
  user: User;
  onUserUpdated: (user: User) => void;
  onAddProfilePhoto: () => void;
};

function VerificationTile({
  icon,
  label,
  verified,
  loading,
  onClick,
  disabled,
  accent = 'blue',
}: {
  icon: string;
  label: string;
  verified: boolean;
  loading?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  accent?: 'blue' | 'green';
}) {
  const filled = verified ? (accent === 'green' ? 'bg-green-50 ring-1 ring-green-200' : 'bg-blue-600') : 'bg-gray-100';
  const iconColor = verified && accent !== 'green' ? 'text-white' : 'text-gray-500';
  const body = (
    <div className="flex w-[72px] flex-col items-center gap-2">
      <div
        className={`flex h-14 w-14 items-center justify-center rounded-full ${filled} transition-colors`}
      >
        {loading ? (
          <i className="ri-loader-4-line animate-spin text-xl text-green-700" aria-hidden />
        ) : verified && accent === 'green' ? (
          <i className="ri-shield-check-fill text-2xl text-green-600" aria-hidden />
        ) : (
          <i className={`${icon} text-2xl ${iconColor}`} aria-hidden />
        )}
      </div>
      <span className="whitespace-pre-line text-center text-[11px] font-medium leading-tight text-gray-700">
        {label}
      </span>
    </div>
  );

  if (!onClick || disabled) {
    return body;
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled || loading} className="shrink-0">
      {body}
    </button>
  );
}

export async function completeIdentityReturn(onUserUpdated: (u: User) => void): Promise<boolean> {
  const sessionId = sessionStorage.getItem(IDENTITY_SESSION_KEY);
  if (!sessionId) return false;
  sessionStorage.removeItem(IDENTITY_SESSION_KEY);
  try {
    const confirm = await authAPI.confirmStripeIdentity(sessionId);
    if (confirm.user) {
      onUserUpdated(confirm.user);
      localStorage.setItem('user', JSON.stringify(confirm.user));
    }
    return confirm.verified === true;
  } catch {
    const me = await authAPI.getMe();
    if (me.user) {
      onUserUpdated(me.user);
      localStorage.setItem('user', JSON.stringify(me.user));
      return me.user.truyou_verified === true;
    }
    return false;
  }
}

const ProfileVerificationSection: React.FC<Props> = ({
  user,
  onUserUpdated,
  onAddProfilePhoto,
}) => {
  const [emailLoading, setEmailLoading] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [phoneStep, setPhoneStep] = useState<'phone' | 'code'>('phone');
  const [phoneLoading, setPhoneLoading] = useState(false);

  const emailVerified = user.email_verified === true;
  const profileVerified = Boolean(user.profile_picture?.trim());
  const phoneVerified = user.phone_verified === true;
  const identityVerified = user.truyou_verified === true;

  const persistUser = (next: User) => {
    onUserUpdated(next);
    localStorage.setItem('user', JSON.stringify(next));
  };

  const handleEmail = async () => {
    if (emailVerified) return;
    setEmailLoading(true);
    try {
      const data = await authAPI.sendVerificationEmail();
      toast.success(data.message || 'Check your inbox for a verification link');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Could not send verification email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePhoneSend = async () => {
    const digits = phoneInput.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Enter a valid US phone number');
      return;
    }
    setPhoneLoading(true);
    try {
      const data = await authAPI.sendPhoneCode(phoneInput);
      toast.success(data.message || 'Code sent');
      setPhoneStep('code');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Could not send code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneVerify = async () => {
    if (codeInput.trim().length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setPhoneLoading(true);
    try {
      const data = await authAPI.verifyPhone(phoneInput, codeInput.trim());
      if (data.user) persistUser(data.user);
      toast.success(data.message || 'Phone verified');
      setPhoneOpen(false);
      setCodeInput('');
      setPhoneStep('phone');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Invalid code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const finishIdentity = async (verificationSessionId: string) => {
    try {
      const confirm = await authAPI.confirmStripeIdentity(verificationSessionId);
      if (confirm.verified && confirm.user) {
        persistUser(confirm.user);
        toast.success('Your profile is verified');
        return;
      }
      const complete = await authAPI.completeStripeIdentity();
      if (complete.verified && complete.user) {
        persistUser(complete.user);
        toast.success('Your profile is verified');
        return;
      }
      toast.success('Verification submitted. Status may take a minute to update.');
      const me = await authAPI.getMe();
      if (me.user) persistUser(me.user);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Could not confirm verification');
    }
  };

  const handleIdentity = async () => {
    if (identityVerified) return;
    setIdentityLoading(true);
    try {
      const session = await authAPI.createStripeIdentitySession();
      const sessionId = session.verification_session_id || '';
      if (sessionId) {
        sessionStorage.setItem(IDENTITY_SESSION_KEY, sessionId);
      }

      if (session.url) {
        window.location.href = session.url;
        return;
      }

      const stripe = await loadStripeClient();
      const clientSecret = session.client_secret;
      if (stripe && clientSecret && typeof (stripe as any).verifyIdentity === 'function') {
        const result = await (stripe as any).verifyIdentity(clientSecret);
        if (result?.error) {
          toast.error(result.error.message || 'Verification cancelled');
          return;
        }
        if (sessionId) await finishIdentity(sessionId);
        return;
      }

      toast.error('Identity verification is not available in the browser. Use the BuyDrop app or try again later.');
    } catch (e: any) {
      const status = e.response?.status;
      if (status === 503) {
        try {
          const data = await authAPI.joinTruYou();
          if (data.user) persistUser(data.user);
          toast.success(data.message || 'Profile verified');
        } catch (joinErr: any) {
          toast.error(joinErr.response?.data?.error || 'Complete email, photo, and phone first');
        }
      } else {
        toast.error(e.response?.data?.error || 'Could not start verification');
      }
    } finally {
      setIdentityLoading(false);
    }
  };

  return (
    <div className="mb-8 pb-8 border-b border-gray-100">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Profile verification</h3>
      <p className="text-sm text-gray-600 mb-4">
        Verify email, photo, phone, and government ID with a selfie so buyers know who they are buying from.
      </p>

      <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-5 px-1 py-2 min-w-min">
          <VerificationTile
            icon="ri-mail-fill"
            label={'Email\nverified'}
            verified={emailVerified}
            loading={emailLoading}
            onClick={handleEmail}
            disabled={emailVerified}
          />
          <VerificationTile
            icon="ri-user-fill"
            label={'Profile\nphoto'}
            verified={profileVerified}
            onClick={onAddProfilePhoto}
            disabled={profileVerified}
          />
          <VerificationTile
            icon="ri-phone-fill"
            label={'Phone\nverified'}
            verified={phoneVerified}
            onClick={() => {
              setPhoneInput(user.phone?.replace(/^\+1/, '') || '');
              setPhoneOpen(true);
            }}
            disabled={phoneVerified}
          />
          <VerificationTile
            icon="ri-fingerprint-line"
            label={'ID &\nselfie'}
            verified={identityVerified}
            loading={identityLoading}
            onClick={handleIdentity}
            disabled={identityVerified}
            accent="green"
          />
        </div>
      </div>

      {phoneOpen && !phoneVerified && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 max-w-md">
          <p className="text-sm font-medium text-gray-900 mb-3">Verify phone</p>
          {phoneStep === 'phone' ? (
            <>
              <input
                type="tel"
                inputMode="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="(555) 555-5555"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base mb-3"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPhoneOpen(false)}
                  className="px-3 py-2 text-sm text-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={phoneLoading}
                  onClick={handlePhoneSend}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Send code
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base mb-3"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPhoneStep('phone')}
                  className="px-3 py-2 text-sm text-gray-600"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={phoneLoading}
                  onClick={handlePhoneVerify}
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileVerificationSection;
